import { isFestivalEditionAvailable } from "./availability";
import type {
  FestivalConfig,
  FestivalState,
  FestivalWeatherYearRow,
  FestivalWeekend,
} from "./types";

const LIVE_FORECAST_CUTOFF_DAYS = 14;
const RECENTLY_FINISHED_WINDOW_DAYS = 60;

export { LIVE_FORECAST_CUTOFF_DAYS };

export type ResolvedWeekend = {
  label: string;
  start: Date;
  end: Date;
  status: "past" | "active" | "upcoming";
};

export type ResolvedEdition = {
  year: number;
  state: FestivalState;
  unavailableReason?: string;
  eventStart: Date;
  eventEnd: Date;
  confirmedDates: boolean;
  // True when state is live_forecast AND today is inside the event window
  // (the festival is actively running). False when state is live_forecast
  // but the event hasn't started yet (within 14-day cutoff).
  happeningNow: boolean;
  // Populated when the festival config has `weekends` defined on the
  // active confirmedDates entry (e.g. Coachella, Tomorrowland).
  weekends?: ResolvedWeekend[];
  // The weekend driving the forecast window: the currently-active weekend
  // if one is running, otherwise the next upcoming one. Undefined if all
  // weekends are past or no weekends are defined.
  forecastWeekend?: ResolvedWeekend;
};

export function isWithinDays(a: Date, b: Date, n: number): boolean {
  const diffMs = Math.abs(b.getTime() - a.getTime());
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= n;
}

function daysDiff(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
}

/**
 * Materialise a festival's typicalWindow as two Dates for the given
 * edition year. For NYE-style windows (monthStart > monthEnd), the
 * event start falls in the previous calendar year.
 */
export function typicalWindowForYear(
  f: FestivalConfig,
  editionYear: number
): { start: Date; end: Date } {
  const w = f.typicalWindow;
  const crossesYearBoundary = w.monthStart > w.monthEnd;
  const startYear = crossesYearBoundary ? editionYear - 1 : editionYear;
  const endYear = editionYear;
  const start = new Date(
    Date.UTC(startYear, w.monthStart - 1, w.dayStart)
  );
  const end = new Date(Date.UTC(endYear, w.monthEnd - 1, w.dayEnd));
  return { start, end };
}

function resolveWeekends(
  today: Date,
  cdWeekends: FestivalWeekend[] | undefined
): { list: ResolvedWeekend[]; forecast?: ResolvedWeekend } {
  if (!cdWeekends || cdWeekends.length === 0) {
    return { list: [] };
  }
  const list: ResolvedWeekend[] = cdWeekends.map((w) => {
    const start = new Date(w.start + "T00:00:00Z");
    const endOfFinalDay = new Date(
      new Date(w.end + "T00:00:00Z").getTime() + 24 * 60 * 60 * 1000 - 1
    );
    let status: ResolvedWeekend["status"];
    if (today < start) status = "upcoming";
    else if (today <= endOfFinalDay) status = "active";
    else status = "past";
    return {
      label: w.label,
      start,
      end: new Date(w.end + "T00:00:00Z"),
      status,
    };
  });
  // Active first; otherwise next upcoming (in source order).
  const forecast =
    list.find((w) => w.status === "active") ??
    list.find((w) => w.status === "upcoming");
  return { list, forecast };
}

/** Resolve actual editions before considering explicitly unconfirmed typical timing. */
export function resolveActiveEdition(
  today: Date,
  festival: FestivalConfig,
  rows: FestivalWeatherYearRow[]
): ResolvedEdition {
  const currentYear = today.getUTCFullYear();
  const dayMs = 86_400_000;
  const confirmed = (festival.confirmedDates ?? [])
    .filter((edition) => isFestivalEditionAvailable(festival, edition.year))
    .sort((a, b) => a.start.localeCompare(b.start));

  // A stored forecast snapshot or an event that has not ended is not an actual.
  const recent = rows
    .filter((row) => row.festival_slug === festival.slug && row.source === "archive"
      && isFestivalEditionAvailable(festival, row.year)
      && today.getTime() >= new Date(`${row.event_end}T00:00:00Z`).getTime() + dayMs
      && daysDiff(new Date(`${row.event_end}T00:00:00Z`), today) <= RECENTLY_FINISHED_WINDOW_DAYS)
    .sort((a, b) => b.event_end.localeCompare(a.event_end))[0];
  if (recent) return {
    year: recent.year, state: "recently_finished",
    eventStart: new Date(`${recent.event_start}T00:00:00Z`),
    eventEnd: new Date(`${recent.event_end}T00:00:00Z`),
    confirmedDates: true, happeningNow: false,
  };

  for (const edition of confirmed) {
    const start = new Date(`${edition.start}T00:00:00Z`);
    const end = new Date(`${edition.end}T00:00:00Z`);
    if (today.getTime() >= end.getTime() + dayMs) continue;
    const { list, forecast } = resolveWeekends(today, edition.weekends);
    const forecastStart = forecast?.start ?? start;
    if (daysDiff(today, forecastStart) <= LIVE_FORECAST_CUTOFF_DAYS) return {
      year: edition.year, state: "live_forecast", eventStart: start, eventEnd: end,
      confirmedDates: true,
      happeningNow: list.length ? list.some((weekend) => weekend.status === "active") : today >= start,
      ...(list.length > 0 && { weekends: list }),
      ...(forecast && { forecastWeekend: forecast }),
    };
  }

  // The edition year belongs to the final date, including New Year crossings.
  // Search past fallow years, and let changed confirmed dates override old habits.
  const candidates: ResolvedEdition[] = [];
  for (let year = currentYear - 1; year <= currentYear + 4; year++) {
    if (!isFestivalEditionAvailable(festival, year)) continue;
    const edition = confirmed.find((entry) => entry.year === year);
    const typical = typicalWindowForYear(festival, year);
    const start = edition ? new Date(`${edition.start}T00:00:00Z`) : typical.start;
    const end = edition ? new Date(`${edition.end}T00:00:00Z`) : typical.end;
    if (today.getTime() >= end.getTime() + dayMs) continue;
    const { list, forecast } = resolveWeekends(today, edition?.weekends);
    candidates.push({
      year, state: "climate", eventStart: start, eventEnd: end,
      confirmedDates: Boolean(edition), happeningNow: false,
      ...(list.length > 0 && { weekends: list }),
      ...(forecast && { forecastWeekend: forecast }),
    });
  }
  candidates.sort((a, b) => a.eventStart.getTime() - b.eventStart.getTime());
  if (candidates[0]) return candidates[0];

  const typical = typicalWindowForYear(festival, currentYear);
  return {
    year: currentYear, state: "climate", eventStart: typical.start, eventEnd: typical.end,
    confirmedDates: false, happeningNow: false,
    unavailableReason: festival.paused?.reason ?? "Dates and location for the next edition have not been confirmed.",
  };
}
